import fetch from 'isomorphic-fetch';
import oboe from 'oboe';
import download from 'downloadjs';
import Cookies from 'universal-cookie';

import storageHelper from './storageHelper';
import logger from './logger';
import { isWallboard } from './pageRules';
import { windowLocation } from './url';

export const CLIENT_ROOT_URL =
  typeof window === 'undefined' || process.env.RUN_ENV === 'test'
    ? process.env.BASE_URL || `http://localhost:${process.env.PORT || 8080}/`
    : '/';

export const API_URL =
  typeof window === 'undefined' || process.env.RUN_ENV === 'test'
    ? process.env.BASE_URL || `http://localhost:${process.env.PORT || 8080}/api`
    : '/api';

export const AUTH_URL =
  typeof window === 'undefined' || process.env.RUN_ENV === 'test'
    ? process.env.BASE_URL || `http://localhost:${process.env.PORT || 8080}/auth`
    : '/auth';

function checkForUpdates(appVer) {
  if (appVer) {
    const oldVer = storageHelper.storage().getItem('ver');
    if (!!oldVer && oldVer !== appVer) {
      storageHelper.storage().setItem('ver', appVer);
      windowLocation() && windowLocation().reload();
    } else if (!oldVer) {
      storageHelper.storage().setItem('ver', appVer);
    }
  }
}

function getHeaders(formData) {
  const headers = formData ? {} : { 'content-type': 'application/json' };
  const wallboard = windowLocation() && isWallboard(windowLocation().pathname);
  const id = wallboard && windowLocation().pathname.split('/').slice(-2)[0];
  const token = getToken();
  const displayToken = getDisplayToken(id);

  if (!token && !displayToken) return headers;

  if (wallboard) {
    headers['Authorization'] = displayToken || `Bearer wallboard_${token}`;
  } else {
    headers['Authorization'] = token !== null ? `Bearer ${token}` : displayToken;
  }

  return headers;
}

function getToken() {
  const host = storageHelper.storage().getItem('host') || null;
  const tokenName = host? 'token_' + window.btoa(host): 'token';
  return storageHelper.storage().getItem(tokenName) || null;
}

function getDisplayToken(id) {
  return storageHelper.storage().getItem(id ? `displayToken_${id}` : 'displayToken') || null;
}

function redirectToLogin() {
  const location = windowLocation();
  if (location) {
    const cookies = new Cookies();
    cookies.set('redirectTo', location.pathname, { path: '/', maxAge: 180 });
    location.replace('/login' + location.search);
  }
}

function redirectToDowntime(reason = 'unknown') {
  logger.error('Redirect to downtime', { reason });
  windowLocation()?.replace(`/downtime?reason=${reason}`);
  return Promise.reject();
}

function handleFailedRequest(reason = 'unknown') {
  logger.error('API call failed after repeat', { reason });
  return Promise.reject();
}

function traceResponse(response, url) {
  try {
    const headers = {};
    response.headers.forEach((value, name) => {
      headers[name] = value;
    });

    logger.error('502 Bad Gateway', { url, headers });
  } catch (err) {
    console.error('502 diagnostics failed', err);
  }
}

function probeHealth() {
  return fetch('/healthcheck', { headers: getHeaders() })
    .then((response) => {
      if (!response.ok) throw new Error();
      return response.json();
    })
    .catch(() => {
      throw new Error('healthcheck-unreachable');
    })
    .then((health) => {
      if (health.database !== 'UP') throw new Error('healthcheck-down-database');
      if (health.cache !== 'UP') throw new Error('healthcheck-down-cache');
      if (health.status !== 'UP') throw new Error('healthcheck-down-status');
      return health;
    });
}

function request(url, method, body, opt = {}, isRetry = false) {
  if (!isRetry && !opt.formData && body) {
    body = JSON.stringify(body);
  }

  return fetch(url, {
    headers: getHeaders(opt.formData),
    method,
    body
  })
    .then(response => {
      checkForUpdates(response.headers.get('X-App-Version'));

      if (response.status === 401) {
        storageHelper.storage().removeItem('token');
        storageHelper.storage().removeItem('displayToken');
        redirectToLogin();
        return Promise.reject();
      }

      if (response.status === 502) {
        traceResponse(response, url);

        if (isRetry) {
          return handleFailedRequest('retry-failed');
        }

        return probeHealth()
          .then(() => ({ retry: true }))
          .catch((err) => redirectToDowntime(err && err.message));
      }

      if (response.redirected) {
        if (url === '/api/account/info' && response.url.includes('/login')) {
          storageHelper.storage().removeItem('token');
          storageHelper.storage().removeItem('displayToken');
          redirectToLogin();
          return Promise.reject();
        }
        windowLocation() && windowLocation().replace(response.url);
        return Promise.reject();
      }

      return response
        .json()
        .then(json => ({ json, response }))
        .catch(err => {
          return {
            json: { message: 'Empty response' },
            response: { ...response, empty: true, ok: false }
          };
        });
    })
    .then(({ json, response, retry }) => {
      if (retry) {
        return request(url, method, body, opt, true);
      }

      if (response.empty) {
        return Promise.reject();
      }
      if (!response.ok) {
        if (json) return Promise.reject(json);
      }

      return json;
    })
    .catch(err => {
      if (err && err.toString().indexOf('Failed to fetch') === -1) {
        return Promise.reject(err);
      }
      return Promise.reject();
    });
}

export function api(endpoint, method = 'get', body = undefined) {
  return request(`${API_URL}/${endpoint}`, method, body);
}

export function apiWithFormData(endpoint, body) {
  return request(`${API_URL}/${endpoint}`, 'post', body, {
    formData: true
  });
}

export function auth(endpoint, method = 'get', body) {
  return request(`${AUTH_URL}/${endpoint}`, method, body);
}

export function stream(endpoint, method = 'get') {
  return oboe({
    url: `${API_URL}/${endpoint}`,
    method: method,
    headers: getHeaders()
  });
}

export function downloadFile(url, name) {
  return fetch(url, {
    method: 'GET',
    responseType: 'blob'
  })
    .then(res => {
      return res.blob();
    })
    .then(blob => {
      return download(blob, name, 'application/pdf');
    });
}
